import type { DeferredMembershipPreviewDto } from "@zipper/embedded-api-client";
import { html, nothing, svg, type TemplateResult } from "lit";
import { formatCents, type OrderTotals } from "../../../checkout/order-totals";
import { formatMajorAmount, formatShortAmount } from "../../format-money";
import { renderCheckoutTotals } from "../checkout-totals.view";

const ICON_LOCK = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z"/><polyline points="9 12 11 14 15 10"/></svg>`;
const ICON_CHECK = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

export interface PurchaseSummaryViewModel {
  image: string;
  productName: string;
  productSub: string;
  price: number;
  recurring: boolean;
  isGift: boolean;
  isDeferred?: boolean;
  deferredPreview?: DeferredMembershipPreviewDto | undefined;
  giftCents: number;
  giftRecipientName: string;
  features: readonly string[];
  promoCode: string;
  promoBusy: boolean;
  promoError: string;
  discount: number;
  subtotal: number;
  total: number;
  /** The server's quote for this cart (tax + pass-on fees), once fetched. */
  quote?: OrderTotals | undefined;
  /** Org ISO-4217 currency; USD when unset. */
  currency?: string | undefined;
  onPromoCodeChange: (value: string) => void;
  onApplyPromo: () => void;
}

export function renderPurchaseSummary(
  model: PurchaseSummaryViewModel,
): TemplateResult {
  return html`
    <aside class="card summary">
      <h3>Order Summary</h3>
      <div class="sum-item">
        ${model.image
          ? html`<div class="thumb"><img src=${model.image} alt="" /></div>`
          : nothing}
        <div style="min-width:0">
          <div class="nm">${model.productName}</div>
          ${model.productSub
            ? html`<div class="mt">${model.productSub}</div>`
            : nothing}
        </div>
        <span class="pr"
          >${model.isGift
            ? model.giftCents
              ? formatShortAmount(model.giftCents / 100, model.currency)
              : "—"
            : html`${formatShortAmount(
                model.price,
                model.currency,
              )}${model.recurring ? "/mo" : ""}`}</span
        >
      </div>
      ${
        model.isGift && model.giftRecipientName
          ? html`<div class="mt">For ${model.giftRecipientName}</div>`
          : nothing
      }
      ${
        model.features.length
          ? html`<div class="features">
              ${model.features.map(
                (feature) =>
                  html`<div class="feature">
                    <span class="fc">${ICON_CHECK}</span> ${feature}
                  </div>`,
              )}
            </div>`
          : nothing
      }
      ${
        model.isGift || model.isDeferred
          ? nothing
          : html`<div class="promo">
              <div class="lbl">Discount code</div>
              <div class="row">
                <input
                  placeholder="Code"
                  .value=${model.promoCode}
                  @input=${(event: Event) =>
                    model.onPromoCodeChange(
                      (event.target as HTMLInputElement).value,
                    )}
                />
                <button
                  ?disabled=${model.promoBusy}
                  @click=${model.onApplyPromo}
                >
                  ${model.promoBusy ? "…" : "Apply"}
                </button>
              </div>
              ${
                model.promoError
                  ? html`<div class="promo-msg err">${model.promoError}</div>`
                  : model.discount
                    ? html`<div class="promo-msg ok">
                        Code applied —
                        −${formatMajorAmount(model.discount, model.currency)}
                      </div>`
                    : nothing
              }
            </div>`
      }
      ${
        model.isDeferred
          ? html`<div class="totals">
              <div class="total">
                <span>Due today</span
                ><span
                  >${model.deferredPreview ? formatCents(0, model.currency) : "Confirming terms…"}</span
                >
              </div>
              ${
                model.deferredPreview
                  ? html`<p>
                        First charge:
                        ${model.deferredPreview.expectedChargeLabel}
                      </p>
                      <p>
                        First payment:
                        ${formatCents(model.deferredPreview.futureChargeAmountInCents, model.currency)}
                      </p>`
                  : nothing
              }
            </div>`
          : renderCheckoutTotals({
              subtotal: model.subtotal,
              discount: model.discount,
              quote: model.quote,
              currency: model.currency,
            })
      }

      <div class="ssl">${ICON_LOCK} Secure SSL Checkout</div>
    </aside>
  `;
}
